import _uuid from 'uuid';
import { diff } from 'deep-object-diff';
import { get, defaultTo } from 'lodash';
// eslint-disable-next-line import/extensions
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { isNaN, isEmail } from 'utils/validations/functions';

export { md5 } from './md5';

/**
 * Utility function to calculate progress percentage with defaults.
 * @param {*} num Number
 * @param {*} den Number
 * @returns Number rounded using Math.floor(<value>);
 * @returns 1 when calculated percent is greater than 0 but less than 1
 * @return 0 when denominator is 0
 */
export function calcPercent(num = null, den = null) {
  const numerator = Number(num);
  const denominator = Number(den);
  if (denominator === 0) {
    return 0;
  }
  const percent = (numerator / denominator) * 100;
  return percent > 0 && percent < 1 ? 1 : Math.floor(percent);
}
export function getObjValue(obj, path, defaultValue) {
  return defaultTo(get(obj, path, defaultValue), defaultValue);
}
/**
 * Compares two POJOs and returns true if there are differences in keys.
 * Treats {} and undefined as same
 * Treats {} and null as same
 * Treats { a: undefined } and { } as same
 * Treats { a: null } and { } as same
 * Treats { a: {} } and { } as same
 * @param {} a
 * @param {} b
 */
export function isDifferent(a, b) {
  const tmpA = a || {};
  const tmpB = b || {};
  const changed = diff(tmpA, tmpB);
  return Object.keys(changed).length > 0;
}
export function sortUsersByCurrentFirst(currentUser) {
  return (a, b) => {
    if (a.email === currentUser.email) {
      return -1;
    }
    if (b.email === currentUser.email) {
      return 1;
    }
    return 0;
  };
}

export const sortObjectArrayByProperty = (arr = [], prop = '') => {
  return arr.sort((a, b) => {
    if (a[prop] < b[prop]) {
      return -1;
    }
    if (a[prop] > b[prop]) {
      return 1;
    }
    return 0;
  });
};

export function stripHTML(str) {
  const string = str || '';
  const div = document.createElement('div');
  div.innerHTML = string;
  return div.textContent || div.innerText || '';
}

const sanitizeTarget = () => {
  const TEMPORARY_ATTRIBUTE = 'data-temp-target';
  DOMPurify.addHook('beforeSanitizeAttributes', node => {
    if (node.tagName?.toLowerCase() === 'a' && node.hasAttribute('target')) {
      node.setAttribute(TEMPORARY_ATTRIBUTE, node.getAttribute('target'));
    }
  });
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName?.toLowerCase() === 'a' && node.hasAttribute(TEMPORARY_ATTRIBUTE)) {
      node.setAttribute('target', node.getAttribute(TEMPORARY_ATTRIBUTE));
      node.removeAttribute(TEMPORARY_ATTRIBUTE);
      if (node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener');
      }
    }
  });
};

export const htmlToReact = input => {
  sanitizeTarget();
  return parse(DOMPurify.sanitize(input));
};

export function uuid() {
  return _uuid.v4();
}
// Must be lower-cased because Cognito user emails are case-sensitive.
export const normalizeEmail = email => email.trim().toLowerCase();

export function isEmptyString(str) {
  return !str || str.trim().length === 0;
}
export function extractEmailDomain(email) {
  if (isEmail(email)) {
    return email.split('@')[1];
  }
  return null;
}

export function hasValue(value) {
  return value !== null && value !== undefined;
}

/**
 * Function that formats a valid numeric String by truncating (not rounding)
 * decimal value
 * @example ('20.12999', 2) -> '20.12'
 * @param {*} value String value of a number
 * @param {*} maxDecimalPlaces Number of allowed decimal places
 */
export function truncateDecimals(value, maxDecimalPlaces) {
  let newValue = value;
  if (hasValue(value) && hasValue(maxDecimalPlaces) && value.includes('.') && !isNaN(value)) {
    const array = value.split('.');
    const integer = array[0];
    let decimals = array[1];
    if (decimals.length >= maxDecimalPlaces) {
      const showDecimal = decimals.length > 0 && maxDecimalPlaces > 0;
      decimals = decimals.substring(0, maxDecimalPlaces);
      newValue = `${integer}${showDecimal ? '.' : ''}${decimals}`;
    }
  }
  return newValue;
}

export function getFileExtension(filename) {
  const arr = filename.split('.');
  if (arr.length > 1) {
    return arr[arr.length - 1].toLowerCase();
  }
  return '';
}

export const promiseTimeout = time => result => new Promise(resolve => setTimeout(resolve, time, result));

export const hasUpperCase = password => {
  const upperCaseRegex = /([A-Z]+)/;
  return upperCaseRegex.test(password);
};

export const hasLowerCase = password => {
  const lowerCaseRegex = /([a-z]+)/;
  return lowerCaseRegex.test(password);
};

export const hasMinChars = password => {
  const minCharRegex = /(?=.{8,})/;
  return minCharRegex.test(password);
};

export const hasNumber = password => {
  const numberRegex = /([0-9]+)/;
  return numberRegex.test(password);
};

export const hasSpecialCharacter = password => {
  const specialCharRegex = /([!@"#$%'()*+,-/:;=?<>^~|`.]+)/;
  return specialCharRegex.test(password);
};

export const hasAllowedCharacters = password => {
  const allowedCharRegex = /^[a-zA-Z\d!@"#$%'()*+,-/:;=?<>^~|`.]+$/;
  return allowedCharRegex.test(password);
};

export const getDateTime = date => {
  const today = new Date();
  let hour = today.getHours();
  let minute = today.getMinutes();
  hour = hour < 10 ? `0${hour}` : hour;
  minute = minute < 10 ? `0${minute}` : minute;
  const time = `${hour}:${minute}:00.000`;
  return `${date}T${time}Z`;
};

export default {
  hasValue,
  normalizeEmail,
  truncateDecimals,
  extractEmailDomain,
  isEmptyString,
  calcPercent,
  getObjValue,
  isDifferent,
  sortUsersByCurrentFirst,
  sortObjectArrayByProperty,
  stripHTML,
  htmlToReact,
  uuid,
  promiseTimeout,
  getFileExtension,
  hasUpperCase,
  hasLowerCase,
  hasMinChars,
  hasNumber,
  hasSpecialCharacter,
  hasAllowedCharacters,
  getDateTime
};
